import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { itIT } from '@clerk/localizations';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import './styles/globals.css';

const publishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!publishableKey) {
  throw new Error('Missing Clerk publishable key');
}

// Stile dei widget Clerk (SignIn/SignUp/UserProfile) allineato al brand:
// niente card/ombre proprie (le mettiamo noi), colore primario rosso-arancio,
// header ridondante nascosto perché il titolo lo mostriamo già nella nostra card.
const clerkAppearance = {
  variables: {
    colorPrimary: '#b5270a',
    colorText: '#1c1c19',
    colorTextSecondary: '#5a403b',
    colorInputText: '#1c1c19',
    colorInputBackground: '#ffffff',
    colorBackground: '#ffffff',
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.95rem',
    borderRadius: '10px',
  },
  elements: {
    rootBox: { width: '100%' },
    cardBox: { width: '100%', boxShadow: 'none', border: 'none' },
    card: { boxShadow: 'none', border: 'none', padding: 0, width: '100%' },
    headerTitle: { display: 'none' },
    headerSubtitle: { display: 'none' },
    footer: { display: 'none' },
    logoBox: { display: 'none' },
    // NB: il nastro "Secured by Clerk" NON è il watermark "development" e NON sparisce
    // in produzione: è il badge di branding di Clerk, obbligatorio sul piano gratuito.
    // Si rimuove solo con un piano Clerk a pagamento (dashboard → Customization → Branding).
    // Nasconderlo via CSS sul piano free viola i ToS di Clerk → non si tocca da qui.
    formFieldLabel: { fontWeight: 600, marginBottom: '4px' },
    formFieldInput: {
      padding: '12px 14px',
      borderColor: '#e3d8d4',
      backgroundColor: '#fff',
    },
    formButtonPrimary: {
      textTransform: 'none',
      fontSize: '0.95rem',
      fontWeight: 700,
      padding: '12px 16px',
      boxShadow: 'none',
      '&:hover': { backgroundColor: '#94200a' },
    },
    socialButtonsBlockButton: { borderColor: '#e3d8d4' },
    dividerLine: { backgroundColor: '#ece3df' },
    formFieldInputShowPasswordButton: { color: '#5a403b' },
  },
};

// ClerkProvider deve stare DENTRO il Router e ricevere `navigate`, così le
// transizioni tra gli step di SignIn/SignUp (email → password, verifica, ...)
// avvengono lato client e non come reload completo della pagina. Senza questa
// prop, Clerk v4 fa una navigazione "hard": la pagina si ricarica e il componente
// si rimonta (causando, in registrazione, un secondo invio del codice).
function ClerkWithRouter() {
  const navigate = useNavigate();
  return (
    <ClerkProvider
      publishableKey={publishableKey}
      navigate={(to) => navigate(to)}
      localization={itIT}
      appearance={clerkAppearance}
    >
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </ClerkProvider>
  );
}

// react-snap prerenderizza le pagine in HTML statico al build: se il #root ha
// già contenuto (pagina prerenderata) si fa hydrate, altrimenti render normale.
const rootElement = document.getElementById('root');
const app = (
  <React.StrictMode>
    <BrowserRouter>
      <ClerkWithRouter />
    </BrowserRouter>
  </React.StrictMode>
);

if (rootElement.hasChildNodes()) {
  ReactDOM.hydrateRoot(rootElement, app);
} else {
  ReactDOM.createRoot(rootElement).render(app);
}
